import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardDocumentCheckIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, Spinner, Modal, SearchInput, Dropdown } from '../../components/ui';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { sysadminService } from '../../services/sysadmin.service';
import { useSysadminStore } from '../../stores';
import { toast } from 'react-toastify';

interface Questionnaire {
  id: string;
  name: string;
  mdiQuestionnaireId?: string;
  questionCount?: number;
  lastSyncedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const formatDate = (dateString?: string) => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const Questionnaires: React.FC = () => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<Questionnaire | null>(null);
  const [form, setForm] = useState({ name: '', mdiQuestionnaireId: '' });

  const { companies, fetchCompanies } = useSysadminStore();

  const fetchQuestionnaires = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await sysadminService.getQuestionnaires();
      setQuestionnaires(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Failed to load questionnaires');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuestionnaires();
    if (companies.length === 0) fetchCompanies();
  }, [fetchQuestionnaires, companies.length, fetchCompanies]);

  const filteredQuestionnaires = questionnaires.filter((q) =>
    q.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = () => {
    setSelectedQuestionnaire(null);
    setForm({ name: '', mdiQuestionnaireId: '' });
    setIsModalOpen(true);
  };

  const handleEdit = (q: Questionnaire) => {
    setSelectedQuestionnaire(q);
    setForm({ name: q.name, mdiQuestionnaireId: q.mdiQuestionnaireId || '' });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (selectedQuestionnaire) {
        await sysadminService.updateQuestionnaire(selectedQuestionnaire.id, form);
        toast.success('Questionnaire updated');
      } else {
        await sysadminService.createQuestionnaire(form);
        toast.success('Questionnaire created');
      }
      setIsModalOpen(false);
      fetchQuestionnaires();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save questionnaire');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this questionnaire?')) return;
    try {
      await sysadminService.deleteQuestionnaire(id);
      toast.success('Questionnaire deleted');
      fetchQuestionnaires();
    } catch {
      toast.error('Failed to delete questionnaire');
    }
  };

  const handleSync = async () => {
    if (!selectedCompanyId) return;
    setIsSyncing(true);
    try {
      await sysadminService.syncQuestionnaires(selectedCompanyId);
      toast.success('Questionnaires synced successfully');
      fetchQuestionnaires();
    } catch {
      toast.error('Failed to sync questionnaires');
    } finally {
      setIsSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50">
            Questionnaires
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400">
            Manage intake questionnaires
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select company...</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSync}
            disabled={!selectedCompanyId || isSyncing}
          >
            <ArrowPathIcon className={`w-4 h-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync From'}
          </Button>
          <Button onClick={handleCreate} size="sm">
            <PlusIcon className="w-4 h-4 mr-1" />
            Create
          </Button>
        </div>
      </div>

      {/* Content */}
      <Card>
        <div className="p-6">
          <div className="mb-6">
            <SearchInput
              placeholder="Search questionnaires..."
              value={searchTerm}
              onChange={setSearchTerm}
            />
          </div>

          {filteredQuestionnaires.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-secondary-400" />
              <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-50">No questionnaires found</h3>
              <p className="mt-1 text-sm text-secondary-500">
                {searchTerm ? 'Try adjusting your search.' : 'Get started by creating or syncing questionnaires.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary-200 dark:border-secondary-700">
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">MDI ID</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Questions</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Last Synced</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Updated</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                  {filteredQuestionnaires.map((q) => (
                    <tr key={q.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50">
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <ClipboardDocumentCheckIcon className="w-5 h-5 text-secondary-400 mr-3" />
                          <span className="text-sm font-medium text-secondary-900 dark:text-secondary-50">{q.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-secondary-600 dark:text-secondary-400">
                        {q.mdiQuestionnaireId || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-secondary-600 dark:text-secondary-400">
                        {q.questionCount || 0}
                      </td>
                      <td className="py-3 px-4 text-sm text-secondary-500 dark:text-secondary-400">
                        {formatDate(q.lastSyncedAt)}
                      </td>
                      <td className="py-3 px-4 text-sm text-secondary-500 dark:text-secondary-400">
                        {formatDate(q.updatedAt)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Dropdown
                          trigger={
                            <button className="p-1 rounded hover:bg-secondary-100 dark:hover:bg-secondary-700">
                              <EllipsisHorizontalIcon className="w-5 h-5 text-secondary-500" />
                            </button>
                          }
                          items={[
                            { label: 'Edit', icon: <PencilIcon className="w-4 h-4" />, onClick: () => handleEdit(q) },
                            { label: 'Delete', icon: <TrashIcon className="w-4 h-4" />, onClick: () => handleDelete(q.id), danger: true },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedQuestionnaire ? 'Edit Questionnaire' : 'Create Questionnaire'}
        size="md"
      >
        <div className="space-y-4 p-1">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">MDI Questionnaire ID</label>
            <input
              type="text"
              value={form.mdiQuestionnaireId}
              onChange={(e) => setForm({ ...form, mdiQuestionnaireId: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>{selectedQuestionnaire ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Questionnaires;
